import { Grid, IconButton, Box, Button, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DownloadForOfflineRoundedIcon from "@mui/icons-material/DownloadForOfflineRounded";
import { useEffect, useState, useCallback } from "react";
import { getPaymentOrder } from "../../services/PaymentOrderService";
import { downloadFilePost } from "../../services/ReportService";
import ReceiptPaymentDialog from "./ReceiptPaymentDialog";
import DatagridCustom from "../General/DatagridCustom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";

export default function ManagerPaymentOrder() {
  const { showToast } = useToast();
  const [paymentOrder, setPaymentOrder] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  const [open, setOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const generateReport = async (rowData) => {
    setLoading(true);
    try {
      const payload = {
        payment_order_id: rowData.id,
      };

      const response = await downloadFilePost("payment/order/report", payload);

      const blob = new Blob([response.data], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const contentDisposition = response.headers["content-disposition"];
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : "ordenes_pago.txt";

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showToast("Ocurrió un error al generar el reporte.", "error")
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (rowData) => {
    setSelectedPayment(rowData);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedPayment(null);
  };

  const fetchPaymentOrder = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;

      const res = await getPaymentOrder({
        page: page + 1,
        perPage: pageSize,
        search: searchTerm,
      });

      setPaymentOrder(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("[ManagerPaymentOrder] Error al cargar órdenes:", error);
      showToast(
        "No se pudieron cargar las órdenes de pago. Intente de nuevo más tarde.",
      "error")
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchTerm, showToast]);

  useEffect(() => {
    fetchPaymentOrder();
  }, [fetchPaymentOrder]);

  const handleFilterModelChange = useCallback((newFilterModel) => {
    setFilterModel(newFilterModel);

    const newSearchTerm = newFilterModel.quickFilterValues?.join(" ") || "";
    setSearchTerm(newSearchTerm);

    setPaginationModel((prev) =>
      prev.page !== 0 ? { ...prev, page: 0 } : prev,
    );
  }, []);

  const columns = [
    { field: "code", headerName: "N° de Orden" },
    {
      field: "legal_file",
      headerName: "Expediente",
      flex: 1,
      renderCell: (params) => {
        const file = params.row.legal_file;
        const code = file?.code ?? "Sin código";

        if (!file?.id) {
          return <Typography variant="body2">{code}</Typography>;
        }

        return (
          <Tooltip title="Ver detalle del expediente" placement="top">
            <Button
              component="a"
              href={`/legal/file/${file.id}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
              sx={{
                minWidth: "auto",
                p: 0,
                fontWeight: 900,
                textTransform: "none",
                justifyContent: "flex-start",
                color: "#7d7d7d",
              }}
            >
              {code}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      field: "date",
      headerName: "Fecha",
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);

        if (isNaN(date.getTime())) {
          return "Fecha inválida";
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
      },
    },
    { field: "cbu_destinity", headerName: "CBU de destino", flex: 1 },
    {
      field: "total_amount",
      headerName: "Monto",
      flex: 1,
      align: "right",
      valueFormatter: (params) =>
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(params),
    },
    { field: "status_type", headerName: "Estado", flex: 1 },
    {
      field: "created_at",
      headerName: "Fecha Creación",
      flex: 1.5,
      renderCell: (params) => {
        const date = new Date(params.value);

        if (isNaN(date.getTime())) {
          return "Fecha inválida";
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${day}/${month}/${year} ${hours}:${minutes}`;
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      sortable: false,
      flex: 1.5,
      align: "right",
      headerAlign: "center",
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handleOpenModal(params.row)}
            disabled={!hasPermission("ver detalle orden de pago")}
          >
            <VisibilityRoundedIcon />
          </IconButton>

          <IconButton
            onClick={() => generateReport(params.row)}
            sx={{
              color: "#eab515ff",
              "&:hover": {
                backgroundColor: "rgba(252, 200, 45, 0.1)",
              },
            }}
            disabled={!hasPermission("descargar orden de pago")}
          >
            <DownloadForOfflineRoundedIcon />
          </IconButton>

          <IconButton
            onClick={() => {
              const legalFileId = params.row.legal_file?.id;
              if (legalFileId) {
                navigate(`/legal/file/${legalFileId}`);
              }
            }}
            sx={{
              color: "#4caf50",
              "&:hover": {
                backgroundColor: "rgba(76, 175, 80, 0.1)",
              },
            }}
            disabled={!hasPermission("ver detalle expediente")}
          >
            <AssignmentIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12 }}>

      </Grid>

      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <DatagridCustom
            rows={paymentOrder}
            columns={columns}
            title="Listado de Ordenes de Pagos"
            description="Visualice las Ordenes de pagos cargadas en el sistema."
            icon={<AssignmentIcon sx={{ fontSize: 32 }} />}
            paginationModel={paginationModel}
            onPaginationChange={setPaginationModel}
            total={total}
            loading={loading}
            filterModel={filterModel}
            onFilterChange={handleFilterModelChange}
            filterMode="server"
            isServerPagination={true}
          />
        </Box>
      </Grid>

      {hasPermission("ver detalle orden de pago") && (
        <ReceiptPaymentDialog
          open={open}
          onClose={handleCloseModal}
          data={selectedPayment}
        />
      )}
    </Grid>
  );
}
